import { Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { isObject } from 'lodash-es';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, Observable, of, Subject, takeUntil } from 'rxjs';
import { ModalService } from '@rocketfinancialcorp/rocket-ui/modal';
import { NoteService } from '@shared/services';
import { AddNoteParams, Note } from '@shared/models';
import { MESSAGE } from '@shared/constants';
import { ErrorUtils } from '@shared/utils';
import { CommonModule } from '@angular/common';
import { ConfirmModalComponent } from '../../confirm-modal/confirm-modal.component';
import { NotesFeedAddNoteComponent } from '../notes-feed-add-note/notes-feed-add-note.component';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { NotesFeedItemComponent } from '../notes-feed-item/notes-feed-item.component';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';

@Component({
  selector: 'app-notes-feed',
  templateUrl: './notes-feed.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, NotesFeedAddNoteComponent, RktIconComponent, NotesFeedItemComponent, RktButtonDirective],
})
export class NotesFeedComponent implements OnInit, OnDestroy {
  noteService = inject(NoteService);

  modalService = inject(ModalService);

  @Input() loading = false;

  @Input() entityId!: Note['entityId'];

  @Input() entityType!: Note['entityType'];

  @Input() businessAccountId!: string;

  addNote = false;

  notes$: Observable<Note[]> = of([]);

  viewType: 'tiles' | 'list' = 'tiles';

  newNoteLoading = false;

  noteAddError = '';

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this.fetchNotes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchNotes() {
    this.noteService
      .getNotes({ entityType: this.entityType, entityId: this.entityId })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.newNoteLoading = false;
        }),
        catchError((errorRes) => {
          ErrorUtils.catchError('noteService.getNotes', errorRes);
          return [];
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notes) => {
          this.notes$ = of(notes);
        },
      });
  }

  onViewTypeClick(): void {
    this.viewType = this.viewType === 'tiles' ? 'list' : 'tiles';
  }

  onNoteAdd({ contentText = '', attachments }: Partial<AddNoteParams>): void {
    this.noteAddError = '';
    this.newNoteLoading = true;

    this.noteService
      .addNote({
        businessAccountId: this.businessAccountId,
        entityId: this.entityId,
        entityType: this.entityType,
        contentText,
        attachments,
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.newNoteLoading = false;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.fetchNotes();
          this.addNote = false;
        },
        error: (error?: string | number | HttpErrorResponse) => {
          this.noteAddError =
            (isObject(error) && error.status === 403) || error === 403 ? MESSAGE.PERMISSION_DENIED : MESSAGE.UNKNOWN_ERROR;
          ErrorUtils.catchError('noteService.addNote', this.noteAddError);
        },
      });
  }

  onNoteAddDiscard(isUnsavedData: boolean) {
    if (!isUnsavedData) {
      this.addNote = false;
      return;
    }

    const modalRef = this.modalService.open(ConfirmModalComponent, {
      className: 'confirm-modal',
    });

    modalRef.componentInstance.title = 'Unsaved Changes';
    modalRef.componentInstance.text =
      'You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.';
    modalRef.componentInstance.confirmBtnText = 'OK';

    return modalRef.result.then(
      (result) => {
        if (result) {
          this.addNote = false;
        }
      },
      () => false,
    );
  }
}
